import { Dispatch, ReactElement, SetStateAction, useState } from "react";
import { kebabCase } from "lodash";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Guid } from "@/Common/Models/Guid";
import { VitalSignPatientData } from "@/Common/Models/VitalSignPatientData";
import { Accordion } from "@/DesignSystem";
import { FlowsheetConstants } from "@/Flowsheets/FlowsheetConstants";
import { FlowsheetRiskFactorService } from "@/Flowsheets/Helpers/FlowsheetRiskFactorService";
import { ImmunizationsCareComponentService } from "@/Flowsheets/Helpers/ImmunizationsCareComponentService";
import { VitalSignsCareComponentService } from "@/Flowsheets/Helpers/VitalSignsCareComponentService";
import { CareComponent } from "@/Flowsheets/Models/CareElements/CareComponent";
import { CareElement } from "@/Flowsheets/Models/CareElements/CareElement";
import { VitalSignCareElement } from "@/Flowsheets/Models/CareElements/VitalSignCareElement";
import { FlowsheetFilterSettings } from "@/Flowsheets/Models/FlowsheetFilterSettings";
import { VitalSignPopup } from "@/Patient/Components/VitalSigns/VitalSignPopup";
import { useGetFlowsheetComponentMapper } from "../../Hooks/useGetFlowsheetComponentMapper";
import { AddImmunizationPopup } from "./CareComponents/AddImmunizationPopup";
import { AddRiskFactorsPopup } from "./CareComponents/AddRiskFactorsPopup";
import { CareComponentFactory } from "./CareComponents/CareComponentFactory";
import { AddCareElementIconButton } from "./CareComponents/CareComponentParts/AddCareElementIconButton";
import { FilterSettingsIconButton } from "./CareComponents/CareComponentParts/FilterSettingsIconButton";

import styles from "./FlowsheetContent.module.scss";

enum FlowsheetPopup {
    None = "",
    AddVitalSign = "AddVitalSignPopup",
    AddRiskFactor = "AddRiskFactorPopup",
    AddImmunization = "AddImmunizationPopup"
}

interface IFlowsheetContentProps {
    isEditable: boolean;
    careComponentData: CareComponent[];
    encounterDate: Date;
    encounterId: Guid;
    filterSettings: FlowsheetFilterSettings
    setCareComponentData: Dispatch<SetStateAction<CareComponent[]>>;
    onCareComponentValidityChange: (id: string, isValid: boolean) => void;
    onFilterSelected: (filterName: string) => void;
    patientContactId: Guid;
}

function getPatientModelData(patientContactId: Guid): VitalSignPatientData | null {
    const model = patientStore.PatientModels[patientContactId];
    if (!model)
        return null;

    return {
        contactId: model.IndividualResponse.Contact_id,
        contactFacetId: model.InactivableResponse.Id,
        dateOfBirth: model.IndividualResponse?.DateOfBirth,
        gender: model.Gender(),
        ethnicityKeys: model.IndividualResponse?.EthnicityKeys,
        isPremature: model.IndividualResponse?.IsBornPremature,
        bornPrematureWeeks: model.IndividualResponse?.BornPrematureWeeks
    };
}
function FlowsheetContent({ isEditable, careComponentData, encounterDate, encounterId, filterSettings, patientContactId, setCareComponentData, onCareComponentValidityChange, onFilterSelected }: Readonly<IFlowsheetContentProps>): ReactElement {
    const { getText } = useTranslation();

    const [openedAddCareElementPopup, setOpenedAddCareElementPopup] = useState<FlowsheetPopup>(FlowsheetPopup.None);

    const mapper = useGetFlowsheetComponentMapper();

    function toggleCareComponent(careComponentName: string): void {
        setCareComponentData(prevState => prevState.map(careComponent => {
            if (careComponent.Name === careComponentName) {
                return {
                    ...careComponent,
                    Expanded: !careComponent.Expanded
                };
            }
            return careComponent;
        }));
    }

    function getActionButtons(name: string): ReactElement[] {
        switch (name) {
            case FlowsheetConstants.LabResults.CareComponentName:
                return getLabResultActionButtons();
            case FlowsheetConstants.VitalSigns.CareComponentName:
                return getVitalSignsActionButtons();
            case FlowsheetConstants.RiskFactors.CareComponentName:
                return getRiskFactorsActionButton();
            case FlowsheetConstants.Immunizations.CareComponentName:
                return getImmunizationActionButton();
            default:
                return [];
        }
    }

    function getLabResultActionButtons(): ReactElement[] {
        return [
            <FilterSettingsIconButton
                careComponentName={FlowsheetConstants.LabResults.CareComponentName}
                key={`${FlowsheetConstants.LabResults.CareComponentName}-section-filterSettingsButton`}
                onClick={e => {
                    e.stopPropagation();
                    onFilterSelected(FlowsheetConstants.LabResults.CareComponentName);
                }}
                size="sm"
            />
        ];
    }

    function getVitalSignsActionButtons(): ReactElement[] {
        const actionsButtons: ReactElement[] = [];

        actionsButtons.push(
            <FilterSettingsIconButton
                careComponentName={FlowsheetConstants.VitalSigns.CareComponentName}
                key={`${FlowsheetConstants.VitalSigns.CareComponentName}-section-filterSettingsButton`}
                onClick={e => {
                    e.stopPropagation();
                    onFilterSelected(FlowsheetConstants.VitalSigns.CareComponentName);
                }}
                size="sm"
            />
        );

        if (isEditable) {
            actionsButtons.unshift(
                <AddCareElementIconButton
                    key={`${FlowsheetConstants.VitalSigns.CareComponentName}-section-addVitalSignButton`}
                    onClick={() => setOpenedAddCareElementPopup(FlowsheetPopup.AddVitalSign)}
                />
            );
        }

        return actionsButtons;
    }

    function getRiskFactorsActionButton(): ReactElement[] {
        const actionsButtons: ReactElement[] = [];

        if (isEditable) {
            actionsButtons.push(
                <AddCareElementIconButton
                    key={`${FlowsheetConstants.RiskFactors.CareComponentName}-section-addRiskFactorButton`}
                    onClick={() => setOpenedAddCareElementPopup(FlowsheetPopup.AddRiskFactor)}
                />
            );
        }

        return actionsButtons;
    }

    function getImmunizationActionButton(): ReactElement[] {
        const actionsButtons: ReactElement[] = [];

        if (isEditable) {
            actionsButtons.push(
                <AddCareElementIconButton
                    key={`${FlowsheetConstants.Immunizations.CareComponentName}-section-addImmunizationButton`}
                    onClick={() => setOpenedAddCareElementPopup(FlowsheetPopup.AddImmunization)}
                />
            );
        }

        return actionsButtons;
    }

    return (
        <div className={styles.flowsheetCareComponentsContainer} data-cy="flowsheet-care-components-container">
            {careComponentData.map(p => (
                <Accordion
                    key={p.Name}
                    dataCy={`flowsheet-${kebabCase(p.Name)}`}
                    title={getText(`Flowsheets_${p.Name}_Title`)}
                    isOpen={p.Expanded}
                    titleSize="title-md"
                    variant="tertiary"
                    actionButtons={getActionButtons(p.Name)}
                    onToggle={() => toggleCareComponent(p.Name)}
                >
                    <div className={styles.careComponentContainer}>
                        <CareComponentFactory
                            careComponentName={p.Name}
                            careComponentData={careComponentData}
                            encounterDate={encounterDate}
                            filterSettings={filterSettings.CareComponentFilterSettings?.find(x => x.Name === p.Name) ?? null}
                            setCareComponentData={setCareComponentData}
                            componentMap={mapper}
                            isEditable={isEditable}
                            onCareComponentValidityChange={onCareComponentValidityChange}
                        />
                    </div>

                </Accordion>
            ))}
            {openedAddCareElementPopup === FlowsheetPopup.AddVitalSign && (
                <VitalSignPopup
                    isAddingToExistingEncounter={true}
                    onClose={(encounterId, vitalSign) => {
                        if (vitalSign) {
                            setCareComponentData(prevData => prevData.map(component => {
                                if (component.Name === FlowsheetConstants.VitalSigns.CareComponentName) {
                                    return {
                                        ...component,
                                        CareElements: VitalSignsCareComponentService.addVitalSignToCareElements(
                                            vitalSign,
                                            component.CareElements as VitalSignCareElement[],
                                            encounterDate
                                        )
                                    };
                                }
                                return component;
                            }));
                        }
                        setOpenedAddCareElementPopup(FlowsheetPopup.None);
                    }}
                    disabledSavePlus={true}
                    patientData={getPatientModelData(patientContactId)}
                />
            )}
            {openedAddCareElementPopup === FlowsheetPopup.AddRiskFactor && (
                <AddRiskFactorsPopup
                    patientId={patientContactId}
                    encounterDate={encounterDate}
                    onClose={() => setOpenedAddCareElementPopup(FlowsheetPopup.None)}
                    onSave={() => {
                        setCareComponentData(prevData => prevData.map(component => {
                            if (component.Name === FlowsheetConstants.RiskFactors.CareComponentName) {
                                return {
                                    ...component,
                                    CareElements: component.CareElements.map(careElement => {
                                        if (careElement.Name === FlowsheetConstants.RiskFactors.SmokingStatusCareElementName) {
                                            careElement = FlowsheetRiskFactorService.refreshSmokingCareElement(
                                                component,
                                                patientContactId,
                                                encounterDate
                                            );
                                        }
                                        if (careElement.Name === FlowsheetConstants.RiskFactors.AlcoholUseCareElementName) {
                                            careElement = FlowsheetRiskFactorService.refreshAlcoholCareElement(
                                                component,
                                                patientContactId,
                                                encounterDate
                                            );
                                        }
                                        return careElement;
                                    })
                                };
                            }
                            return component;
                        }));

                        setOpenedAddCareElementPopup(FlowsheetPopup.None);
                    }}
                />
            )}
            {openedAddCareElementPopup === FlowsheetPopup.AddImmunization && (
                <AddImmunizationPopup
                    patientId={patientContactId}
                    encounterDate={encounterDate}
                    encounterId={encounterId}
                    onClose={() => setOpenedAddCareElementPopup(FlowsheetPopup.None)}
                    onSave={() => {
                        function cloneCareElements(careElements: CareElement[]): CareElement[] {
                            return careElements.map(careElement => ({ ...careElement }));
                        }

                        setCareComponentData(prevData => prevData.map(component => {
                            if (component.Name === FlowsheetConstants.Immunizations.CareComponentName) {
                                // Clone care elements to avoid mutating the original data which would prevent ImmunizationsCareComponent to rerender correctly
                                const careElements = cloneCareElements(component.CareElements);
                                const newImmunizationCareComponent = { ...component, CareElements: careElements } as CareComponent;

                                new ImmunizationsCareComponentService().processCareComponent(
                                    newImmunizationCareComponent,
                                    patientContactId,
                                    encounterDate
                                );

                                return newImmunizationCareComponent;
                            }

                            return component;
                        }));

                        setOpenedAddCareElementPopup(FlowsheetPopup.None);
                    }}
                />
            )}
        </div>
    );
}

export { FlowsheetContent };
